import {useEffect, useMemo} from 'react';
import {useParams, useSearchParams} from 'react-router-dom';
import {useShallow} from 'zustand/react/shallow';
import raiseFlashMessage from '@/utils/raiseFlashMessage';
import translate from '@/utils/translate';
import {
  PHASE,
  Phase,
  ALL_STATES_FILTER,
} from '@/config/contentSubmissionPhases';
import type {ContentSubmissionKind} from '@/types/contentSubmission';
import {ContentSubmissionSort, SortDir} from '@/types/sorting';
import type {ViewType} from '@/types/viewType';
import {VIEW_TYPE_GRID} from '@/types/viewType';
import {
  ContentSubmissionState,
  ContentSubmissionsMetaStats,
} from '@/api/fetchers/campaignContentSubmission';
import useCampaignContentSubmissionsMetaQuery from '@/hooks/queries/use-campaign-content-submissions-meta-query';
import useCampaignContentSubmissionsQuery from '@/hooks/queries/use-campaign-content-submissions-query';
import useCampaignContractsQuery from '@/hooks/queries/use-campaign-contracts-query';
import useCampaignQuery from '@/hooks/queries/use-campaign-query';
import useGuardedFetchNextPage from '@/hooks/use-guarded-fetch-next-page/useGuardedFetchNextPage';
import useContentSubmissionsStore, {
  contentSubmissionsActions,
} from '@/stores/content/content-submissions/contentSubmissionsStore';
import type {CampaignCreator} from '../types/campaignCreator';
import useContentSubmissionStatusOptions from './useContentSubmissionStatusOptions';
import useRestoreFiltersFromUrl from './useRestoreFiltersFromUrl';

/** Maps each sidebar phase to its API state filter and meta stats key. */
const PHASE_ITEMS: {
  id: Phase;
  localeLabelKey: string;
  stateFilter?: ContentSubmissionState;
  metaKey: keyof ContentSubmissionsMetaStats;
}[] = [
  {
    id: PHASE.ALL,
    localeLabelKey: 'All',
    metaKey: 'totalContentSubmissionsCount',
  },
  {
    id: PHASE.PENDING,
    localeLabelKey: 'Awaiting Review',
    stateFilter: 'pending',
    metaKey: 'totalPendingCount',
  },
  {
    id: PHASE.REJECTED,
    localeLabelKey: 'Changes Requested',
    stateFilter: 'rejected',
    metaKey: 'totalRejectedCount',
  },
  {
    id: PHASE.ACCEPTED,
    localeLabelKey: 'Approved',
    stateFilter: 'accepted',
    metaKey: 'totalAcceptedCount',
  },
  {
    id: PHASE.ARCHIVED,
    localeLabelKey: 'Archived',
    stateFilter: 'archived',
    metaKey: 'totalArchivedCount',
  },
];

/**
 * Manages all state for the Content Submissions tab.
 *
 * - **Phase selection** is stored in the URL search param `?phase=` so it's
 *   shareable and survives refresh. Defaults to "All" when the param is absent.
 * - **Filters** (content type, talent, status) are stored in Zustand and synced
 *   to/from URL search params so they survive refresh and are shareable.
 * - **Content submissions** are fetched with infinite pagination (50 per page),
 *   filtered by the active phase and any applied filters.
 * - **Sidebar counts** come from a separate lightweight query (perPage=1)
 *   filtered by active content type, talent, and status filters, but across
 *   all phases so counts stay accurate regardless of which phase is selected.
 */
function useContentSubmissionsTabState() {
  const {campaignId} = useParams<{campaignId: string}>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPhase = (searchParams.get('phase') ?? PHASE.ALL) as Phase;
  const isOnAllTab = selectedPhase === PHASE.ALL;

  const viewType = (searchParams.get('viewType') ?? VIEW_TYPE_GRID) as ViewType;

  const setViewType = (type: ViewType) => {
    setSearchParams(
      (prev) => {
        if (type === VIEW_TYPE_GRID) {
          prev.delete('viewType');
        } else {
          prev.set('viewType', type);
        }
        return prev;
      },
      {replace: true}
    );
  };

  const sortBy = (searchParams.get('sortBy') ?? undefined) as
    | ContentSubmissionSort
    | undefined;
  const sortDir = (searchParams.get('sortDir') ?? undefined) as
    | SortDir
    | undefined;

  const setSorting = (
    field: ContentSubmissionSort | undefined,
    dir: SortDir | undefined
  ) => {
    setSearchParams(
      (prev) => {
        if (field) {
          prev.set('sortBy', field);
          prev.set('sortDir', dir ?? 'asc');
        } else {
          prev.delete('sortBy');
          prev.delete('sortDir');
        }
        return prev;
      },
      {replace: true}
    );
  };

  /** Updates the `?phase=` search param; removes it entirely when selecting "All". */
  const setSelectedPhase = (phase: Phase) => {
    setSearchParams(
      (prev) => {
        if (phase === PHASE.ALL) {
          prev.delete('phase');
        } else {
          prev.set('phase', phase);
        }
        return prev;
      },
      {replace: true}
    );
  };

  const [isFilterDrawerOpen, appliedFilters] = useContentSubmissionsStore(
    useShallow((state) => [state.isFilterDrawerOpen, state.appliedFilters])
  );

  const activeFilterCount = useMemo(
    () => contentSubmissionsActions.getActiveFilterCount(appliedFilters),
    [appliedFilters]
  );

  const activeFiltersKey = useMemo(
    () =>
      JSON.stringify({
        selectedPhase,
        contentType: appliedFilters.contentType.map((o) => o.id),
        talent: appliedFilters.talent?.id ?? null,
        status: appliedFilters.status.map((s) => s.id),
      }),
    [selectedPhase, appliedFilters]
  );

  const selectedKind =
    appliedFilters.contentType.length > 0
      ? (appliedFilters.contentType
          .map((o) => o.value)
          .join(',') as ContentSubmissionKind)
      : undefined;

  const activePhase = PHASE_ITEMS.find((p) => p.id === selectedPhase);

  const {campaign} = useCampaignQuery({
    campaignId: campaignId ?? '',
    enabled: !!campaignId,
  });

  const {data: contractsData, isLoading: isLoadingContracts} =
    useCampaignContractsQuery({
      campaignId: campaignId ?? '',
    });

  const campaignCreators: CampaignCreator[] = useMemo(() => {
    const contracts = contractsData ?? [];
    return contracts.map((contract) => ({
      id: contract.profile?.id ?? contract.id,
      name: contract.profile?.displayName ?? '',
    }));
  }, [contractsData]);

  const {statusOptions} = useContentSubmissionStatusOptions(
    campaign?.campaignApprovalSteps ?? []
  );

  // Filters only apply on the All tab; non-all tabs show their full phase.
  const activeKind = isOnAllTab ? selectedKind : undefined;
  const activeProfileId = isOnAllTab ? appliedFilters.talent?.id : undefined;
  const activeStateFilter =
    isOnAllTab && appliedFilters.status.length > 0
      ? appliedFilters.status.map((s) => s.id).join(',')
      : (activePhase?.stateFilter ?? ALL_STATES_FILTER);

  // Sidebar counts: always passes status filter so the "All" count stays filtered.
  const sidebarStateFilter =
    appliedFilters.status.length > 0
      ? appliedFilters.status.map((s) => s.id).join(',')
      : undefined;

  // Sidebar counts -- always passes filters (regardless of active tab) so the
  // BE can compute a filtered "All" count while keeping per-phase counts global.
  const {meta, isLoading: isLoadingMeta} = useCampaignContentSubmissionsQuery({
    campaignId: campaignId ?? '',
    enabled: !!campaignId,
    state: sidebarStateFilter,
    kind: selectedKind,
    profileId: appliedFilters.talent?.id,
    perPage: 1,
    staleTime: Infinity,
    include: [],
  });

  // Main paginated query filtered by the active phase and applied filters.
  const {
    contentSubmissions,
    meta: phaseMeta,
    isLoading,
    isError,
    fetchNextPage,
    isFetching,
    isFetchingNextPage,
  } = useCampaignContentSubmissionsQuery({
    campaignId: campaignId ?? '',
    enabled: !!campaignId,
    state: activeStateFilter,
    kind: activeKind,
    profileId: activeProfileId,
    perPage: 50,
    staleTime: Infinity,
    include: [
      'profile',
      'versions.approvals',
      'campaign.campaign_approval_steps',
    ],
    sortBy,
    sortDir,
  });

  useCampaignContentSubmissionsMetaQuery({
    campaignId: campaignId ?? '',
    enabled: !!campaignId,
    state: activeStateFilter,
    kind: activeKind,
    profileId: activeProfileId,
    staleTime: Infinity,
    sortBy,
    sortDir,
  });

  useEffect(() => {
    if (!isError) {
      return;
    }
    raiseFlashMessage({
      status: 'error',
      message: translate('Something went wrong. Please try again.'),
    });
  }, [isError]);

  const guardedFetchNextPage = useGuardedFetchNextPage(
    fetchNextPage,
    isFetching
  );

  const sidebarItems = PHASE_ITEMS.map((phase) => ({
    id: phase.id,
    label: translate(phase.localeLabelKey),
    count: meta?.stats?.[phase.metaKey] ?? 0,
  }));

  useRestoreFiltersFromUrl({
    campaignCreators,
    isLoadingContracts,
    statusOptions,
  });

  const phaseTotalCount = activePhase
    ? (phaseMeta?.stats?.[activePhase.metaKey] ?? undefined)
    : undefined;

  return {
    campaignId,
    campaign,
    campaignCreators,
    contentSubmissions,
    meta: phaseMeta,
    phaseTotalCount,
    isLoading,
    isLoadingMeta,
    isError,
    sidebarItems,
    selectedPhase,
    isOnAllTab,
    setSelectedPhase,
    isFilterDrawerOpen,
    activeFilterCount,
    activeFiltersKey,
    guardedFetchNextPage,
    isFetchingNextPage,
    viewType,
    setViewType,
    sortBy,
    sortDir,
    setSorting,
  };
}

export default useContentSubmissionsTabState;
